import React, { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import SignInButton from '../auth/SignInButton';
import { useCurrentUser } from '../auth/useCurrentUser';
import { setActiveTenantId } from '../auth/msalConfig';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

export default function AcceptInvitePage({ inviteCode, onAccepted }) {
  const isAuthenticated = useIsAuthenticated();
  const { refresh } = useCurrentUser();
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !inviteCode || status !== 'idle') return;
    (async () => {
      setStatus('submitting');
      try {
        const res = await fetch(`${API}/api/v1/iam/me/accept-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inviteCode }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Accept failed: ${res.status}`);
        }
        setStatus('done');
        // Refresh /me/tenants and let the user land in the new tenant.
        await refresh();
        if (onAccepted) onAccepted();
      } catch (e) {
        setError(e.message);
        setStatus('error');
      }
    })();
  }, [isAuthenticated, inviteCode, status, refresh, onAccepted]);

  if (!inviteCode) {
    return <Wrap title="Invalid invite link" detail="The link is missing an invite code." />;
  }

  if (!isAuthenticated) {
    return (
      <Wrap title="Accept your invitation" detail="Sign in with the email the invite was sent to.">
        <SignInButton style={{ marginTop: 14, padding: '10px 18px' }}>Sign in to continue</SignInButton>
      </Wrap>
    );
  }

  if (status === 'submitting') return <Wrap title="Activating your access…" detail="Please wait." />;
  if (status === 'error') return <Wrap title="Could not accept the invitation" detail={error} />;
  if (status === 'done') {
    return (
      <Wrap title="You're in." detail="The tenant has been added to your account.">
        <button className="btn-primary" style={{ marginTop: 14 }} onClick={() => { window.location.href = '/'; }}>
          Continue
        </button>
      </Wrap>
    );
  }
  return null;
}

function Wrap({ title, detail, children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 480, padding: 32, background: '#fff', border: '1px solid #E8ECF0', borderRadius: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#7A8899', lineHeight: 1.6 }}>{detail}</div>
        {children}
      </div>
    </div>
  );
}
